import type { EmailT } from '~/types'

export type PreflightStatus = 'pass' | 'warn' | 'fail'
export interface PreflightCheck {
  id: string
  label: string
  status: PreflightStatus
  detail: string
}
export interface PreflightResult {
  checks: PreflightCheck[]
  fails: number
  warns: number
}

// Gmail clips messages larger than ~102KB. Stay comfortably under.
const GMAIL_CLIP_BYTES = 102_000
const SUBJECT_MAX = 60
const PREVIEW_MAX = 140

/**
 * Static preflight analysis of a rendered email. Pure string/DOM inspection — no network.
 * Catches the mistakes that most often get emails filtered or that look broken in the
 * inbox: unresolved merge tags / template vars, placeholder or empty links, missing alt
 * text, oversized HTML, and subject/preview-text length.
 */
export function runEmailPreflight(html: string, email: Pick<EmailT, 'subject' | 'previewText'>): PreflightResult {
  const checks: PreflightCheck[] = []
  const safeHtml = html || ''

  // --- Subject line ---
  const subject = (email.subject || '').trim()
  if (!subject) {
    checks.push({ id: 'subject', label: 'Subject line', status: 'fail', detail: 'No subject line set.' })
  } else if (subject.length > SUBJECT_MAX) {
    checks.push({ id: 'subject', label: 'Subject line', status: 'warn', detail: `${subject.length} chars — may truncate in the inbox (aim ≤ ${SUBJECT_MAX}).` })
  } else {
    checks.push({ id: 'subject', label: 'Subject line', status: 'pass', detail: `${subject.length} chars.` })
  }

  // --- Preview text ---
  const preview = (email.previewText || '').trim()
  if (!preview) {
    checks.push({ id: 'preview', label: 'Preview text', status: 'warn', detail: 'Empty — inbox clients will pull the first body text instead.' })
  } else if (preview.length > PREVIEW_MAX) {
    checks.push({ id: 'preview', label: 'Preview text', status: 'warn', detail: `${preview.length} chars — most clients show ~${PREVIEW_MAX}.` })
  } else {
    checks.push({ id: 'preview', label: 'Preview text', status: 'pass', detail: `${preview.length} chars.` })
  }

  // --- BetterBlast template variables (should have been resolved at build time) ---
  // Ignore HTML comments — wrappers often document the {{var}} syntax in a comment.
  const htmlNoComments = safeHtml.replace(/<!--[\s\S]*?-->/g, '')
  const templateVars = [...new Set((htmlNoComments.match(/\{\{[^}]+\}\}|###[^#]+###/g) || []))]
  if (templateVars.length) {
    checks.push({
      id: 'template-vars',
      label: 'Template variables',
      status: 'fail',
      detail: `${templateVars.length} unresolved: ${templateVars.slice(0, 4).join(', ')}${templateVars.length > 4 ? '…' : ''} — set them in the wrapper or email vars.`,
    })
  } else {
    checks.push({ id: 'template-vars', label: 'Template variables', status: 'pass', detail: 'All resolved.' })
  }

  // --- Mailchimp merge tags (resolved by Mailchimp on send, not by BetterBlast) ---
  const mergeTags = [...new Set((safeHtml.match(/\*\|[^|]+\|\*/g) || []))]
  if (mergeTags.length) {
    checks.push({
      id: 'merge-tags',
      label: 'Mailchimp merge tags',
      status: 'warn',
      detail: `${mergeTags.length} present — Mailchimp fills these on send, but they appear literally in a raw test send.`,
    })
  }

  // --- Links ---
  // Ignore hrefs that are themselves merge tags — Mailchimp resolves those (e.g. *|UNSUB|*).
  const hrefs = [...safeHtml.matchAll(/href\s*=\s*["']([^"']*)["']/gi)]
    .map((m) => (m[1] || '').trim())
    .filter((h) => !/^\*\|/.test(h) && !/^mailto:/i.test(h))
  const emptyLinks = hrefs.filter((h) => !h || h === '#' || /^https?:\/\/$/i.test(h))
  const placeholderLinks = hrefs.filter((h) => /example\.com/i.test(h) || /\{\{|###/.test(h))
  const badCount = emptyLinks.length + placeholderLinks.length
  if (!hrefs.length) {
    checks.push({ id: 'links', label: 'Links', status: 'warn', detail: 'No real links found — is that intended?' })
  } else if (badCount) {
    checks.push({ id: 'links', label: 'Links', status: 'warn', detail: `${badCount} of ${hrefs.length} link(s) are empty, "#", or placeholder URLs — confirm before sending.` })
  } else {
    checks.push({ id: 'links', label: 'Links', status: 'pass', detail: `${hrefs.length} link(s), all real URLs.` })
  }

  // --- Image alt text ---
  const imgTags = safeHtml.match(/<img\b[^>]*>/gi) || []
  const missingAlt = imgTags.filter((tag) => {
    const alt = tag.match(/\balt\s*=\s*["']([^"']*)["']/i)
    return !alt || !(alt[1] || '').trim()
  })
  if (!imgTags.length) {
    checks.push({ id: 'alt', label: 'Image alt text', status: 'pass', detail: 'No images.' })
  } else if (missingAlt.length) {
    checks.push({ id: 'alt', label: 'Image alt text', status: 'warn', detail: `${missingAlt.length} of ${imgTags.length} image(s) missing alt text (hurts accessibility & image-blocked inboxes).` })
  } else {
    checks.push({ id: 'alt', label: 'Image alt text', status: 'pass', detail: `All ${imgTags.length} image(s) have alt text.` })
  }

  // --- HTML weight (Gmail clipping) ---
  const bytes = new Blob([safeHtml]).size
  const kb = Math.round(bytes / 1024)
  if (bytes > GMAIL_CLIP_BYTES) {
    checks.push({ id: 'size', label: 'HTML size', status: 'warn', detail: `${kb}KB — over Gmail's ~102KB clip threshold; the footer may be hidden behind "view entire message".` })
  } else {
    checks.push({ id: 'size', label: 'HTML size', status: 'pass', detail: `${kb}KB — under Gmail's clip threshold.` })
  }

  return {
    checks,
    fails: checks.filter((c) => c.status === 'fail').length,
    warns: checks.filter((c) => c.status === 'warn').length,
  }
}
